import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Badge } from 'reactstrap';
import { useAgentSocket } from '../../hooks/useAgentSocket';
import { DataContext } from '../../context/dataContext';
import './chat.scss';
import { getConversations, getConversationMessages } from '../../helpers/chat_helper'
import { stopNotificationSound } from '../../utils/notificationSound'
import ConversationList from './ConversationList'
import MessagePanel from './MessagePanel'
import CustomerInfo from './CustomerInfo'

const Chat = () => {
  // Get JWT token from DataContext (same as other pages)
  const { token } = useContext(DataContext)

  // Fallback to localStorage if context doesn't have it yet
  const authToken = token || localStorage.getItem('token')

  console.log('Chat component mounted, token:', authToken ? 'Found' : 'Not found')

  // Socket.IO hook
  const {
    isConnected,
    user,
    agentStatus, // Get status from hook
    newConversations,
    incomingMessages,
    typingUsers,
    pendingConversations,
    statusChanges,
    sendMessage,
    setTyping,
    changeStatus,
    takeConversation,
    transferConversation,
    closeConversation,
    markAsRead,
    joinConversation,
    clearIncomingMessages,
    clearPendingConversations,
    clearStatusChanges
  } = useAgentSocket(authToken)

  // State
  const [conversations, setConversations] = useState([])
  const [activeConversation, setActiveConversation] = useState(null)
  const [loading, setLoading] = useState(true)
  // removed local agentStatus state
  const [statusFilter, setStatusFilter] = useState('active') // 'active' or 'all'

  /*
   * Modifying loadConversations to preserve locally added "new" conversations.
   * When switching tabs or reloading, the API might not yet return the very latest
   * conversation that came in via socket (race condition).
   * We merge the API response with any conversations currently in state that are marked as 'isNew'.
   */
  const loadConversations = useCallback(async () => {
    try {
      setLoading(true)
      const statusParam = statusFilter === 'active' ? 'assigned,open,pending' : undefined
      // console.log('Status param:', statusParam)
      const response = await getConversations(statusParam ? { status: statusParam } : {})

      // Handle different response structures
      let conversationData = []
      if (response && response.data) {
        if (Array.isArray(response.data)) {
          conversationData = response.data
        } else if (response.data.conversations) {
          conversationData = response.data.conversations
        } else if (response.data.data) {
          conversationData = response.data.data
        }
      }

      console.log('API returned conversations:', conversationData.length)

      setConversations(prevConversations => {
        // Identify "new" conversations in the current state that might be missing from API
        // We trust the socket for immediate updates ("isNew" flag)
        const localNewConversations = prevConversations.filter(c => c.isNew)

        if (localNewConversations.length > 0) {
          console.log('Preserving local new conversations:', localNewConversations.length)

          // Create a Map of API conversations for O(1) lookup
          const apiConversationIds = new Set(conversationData.map(c => c.id))

          // Find which local ones are NOT in the API response
          const missingLocals = localNewConversations.filter(c => !apiConversationIds.has(c.id))

          if (missingLocals.length > 0) {
            console.log('Merging missing local conversations:', missingLocals)
            // Prepend missing locals to the API data
            return [...missingLocals, ...conversationData]
          }
        }

        return conversationData || []
      })

    } catch (error) {
      console.error('Error loading conversations:', error)
      console.error('Error details:', error.response?.data || error.message)
      // Even on error, try to keep what we have if it's "new"
      setConversations(prev => prev.filter(c => c.isNew))
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  // Load conversations on mount and when filter changes
  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // Add new conversations from socket
  useEffect(() => {
    if (newConversations.length > 0) {
      console.log('🆕 New conversations received:', newConversations)

      // Join conversation rooms for assigned conversations
      newConversations.forEach(conv => {
        // If conversation is assigned to current user, join it
        if (conv.assigned_agent_id || conv.status === 'assigned') {
          console.log('Auto-joining conversation room:', conv.id)
          joinConversation(conv.id, (response) => {
            if (response.success) {
              console.log('Successfully joined conversation room:', conv.id)
            } else {
              console.error('Failed to join conversation room:', response.error)
            }
          })
        }
      })

      setConversations(prev => {
        // Merge new conversations, avoiding duplicates
        const existingIds = new Set(prev.map(c => c.id))
        const uniqueNew = newConversations
          .filter(c => !existingIds.has(c.id))
          .map(c => ({ ...c, isNew: true })) // Mark as new for blinking effect
        return [...uniqueNew, ...prev]
      })
    }
  }, [newConversations, joinConversation])

  // Add pending conversations from socket
  useEffect(() => {
    if (pendingConversations.length > 0) {
      console.log('🔔 Pending conversations received:', pendingConversations)
      setConversations(prev => {
        const updated = [...prev]
        pendingConversations.forEach(pendingConv => {
          const index = updated.findIndex(c => c.id === pendingConv.id)
          if (index !== -1) {
            // Update existing conversation
            updated[index] = { ...updated[index], ...pendingConv, status: 'pending' }
          } else {
            // Add new conversation
            updated.unshift(pendingConv)
          }
        })
        return updated
      })
      clearPendingConversations()
    }
  }, [pendingConversations, clearPendingConversations])

  // Handle status changes from socket
  useEffect(() => {
    if (statusChanges.length > 0) {
      console.log('🔄 Status changes received:', statusChanges)
      setConversations(prev => {
        const updated = [...prev]
        statusChanges.forEach(change => {
          const index = updated.findIndex(c => c.id === change.conversation_id)
          if (index !== -1) {
            updated[index] = {
              ...updated[index],
              status: change.status,
              assigned_agent_id: change.assigned_agent_id,
              assignedAgent: change.assignedAgent || updated[index].assignedAgent, // Preserve existing if not provided, or update
              // Update transferredTo if this conversation was transferred and now has a new agent
              transferredTo: updated[index].transferredTo && change.assignedAgent
                ? change.assignedAgent
                : updated[index].transferredTo
            }
          }
        })
        return updated
      })
      clearStatusChanges()
    }
  }, [statusChanges, clearStatusChanges])

  // Auto-refresh conversations when messages arrive for unknown conversations
  // Auto-refresh logic removed to prevent state overwrite race condition
  // New conversations are now handled by the message processing useEffect below which adds them manually with isNew flag
  /* 
   * Previous logic:
   * useEffect(() => { ... loadConversations() ... })
   * This was causing blinking to stop because loadConversations() would overwrite the local state
   */

  // Handle incoming messages
  useEffect(() => {
    if (incomingMessages.length > 0) {
      console.log('📨 Processing incoming messages:', incomingMessages)

      // Process all incoming messages
      setConversations(prevConversations => {
        const updatedConversations = [...prevConversations]

        incomingMessages.forEach(incomingMsg => {
          console.log('📩 Raw incoming message:', incomingMsg)

          // Parse the message structure
          // Backend might send:
          // Option 1: { conversation_id, message: {...} }
          // Option 2: { conversation: {...}, message: {...} } (current backend)
          // Option 3: Just the message object itself with conversation_id inside
          let conversationId = incomingMsg.conversation_id
            || incomingMsg.conversation?.id
            || incomingMsg.message?.conversation_id
          let messageObj = incomingMsg.message || incomingMsg

          // Ensure messageObj has required fields and attachments is an array
          if (messageObj && typeof messageObj === 'object') {
            messageObj = {
              ...messageObj,
              attachments: Array.isArray(messageObj.attachments) ? messageObj.attachments : []
            }
          }

          console.log('✅ Parsed message:', { conversationId, messageObj })

          // Find conversation by ID (handle string/number mismatch)
          const index = updatedConversations.findIndex(c => c.id == conversationId)

          if (index !== -1) {
            const conv = updatedConversations[index]
            // Check if message already exists to avoid duplicates
            const messageExists = conv.messages?.some(m => m.id === messageObj.id)
            if (!messageExists) {
              // Check if this is from a customer (not from agent)
              const isFromCustomer = messageObj.sender_type === 'customer'
              // Check if this conversation is NOT currently active
              const isNotActive = activeConversation?.id != conversationId

              updatedConversations[index] = {
                ...conv,
                messages: [...(conv.messages || []), messageObj],
                updated_at: messageObj.created_at || new Date().toISOString(),
                // Increment unread count if message is from customer and conversation is not active
                unread_count: (isFromCustomer && isNotActive)
                  ? (conv.unread_count || 0) + 1
                  : conv.unread_count || 0,
                // Mark as new for visual indication
                isNew: isFromCustomer && isNotActive
              }

              console.log('✅ Added message to conversation:', conversationId, {
                isFromCustomer,
                isNotActive,
                isNew: updatedConversations[index].isNew,
                unread_count: updatedConversations[index].unread_count
              })

              // Move to top of list if new message from customer and not active
              if (isFromCustomer && isNotActive) {
                const [updated] = updatedConversations.splice(index, 1)
                updatedConversations.unshift(updated)
                console.log('📌 Moved conversation to top of list with blinking enabled')
              }
            } else {
              console.log('⚠️ Message already exists, skipping:', messageObj.id)
            }
          } else {
            // Conversation not found - add it to the list
            console.warn('⚠️ Conversation not found in list:', conversationId)

            if (incomingMsg.conversation) {
              console.log('✅ Adding conversation to list from message data')
              const isFromCustomer = messageObj.sender_type === 'customer'

              const newConv = {
                ...incomingMsg.conversation,
                messages: [messageObj],
                unread_count: isFromCustomer ? 1 : 0,
                isNew: isFromCustomer // Enable blinking for customer messages
              }

              updatedConversations.unshift(newConv)
              console.log('📌 Added new conversation to top of list with blinking:', {
                conversationId,
                isNew: newConv.isNew
              })
            } else {
              console.log('💡 Tip: The conversation might be in the queue or not loaded yet. Try refreshing the conversation list.')
            }
          }
        })

        return updatedConversations
      })

      // Update active conversation if it matches any incoming message
      setActiveConversation(prevActive => {
        if (!prevActive) return prevActive

        const relevantMessages = incomingMessages
          .map(msg => {
            let conversationId = msg.conversation_id
            let messageObj = msg.message || msg

            // Ensure attachments is an array
            if (messageObj && typeof messageObj === 'object') {
              messageObj = {
                ...messageObj,
                attachments: Array.isArray(messageObj.attachments) ? messageObj.attachments : []
              }
            }

            return { conversationId, messageObj }
          })
          .filter(item => item.conversationId == prevActive.id)
          .map(item => item.messageObj)

        if (relevantMessages.length === 0) return prevActive

        // Filter out duplicates
        const existingIds = new Set((prevActive.messages || []).map(m => m.id))
        const newMessages = relevantMessages.filter(m => !existingIds.has(m.id))

        if (newMessages.length === 0) return prevActive

        console.log('Adding messages to active conversation:', newMessages)

        return {
          ...prevActive,
          messages: [...(prevActive.messages || []), ...newMessages]
        }
      })

      // Clear processed messages to avoid re-processing
      clearIncomingMessages()
    }
  }, [incomingMessages, clearIncomingMessages])

  const handleStatusChange = (status) => {
    changeStatus(status)
  }

  const handleSelectConversation = async (conversation) => {
    setActiveConversation(conversation)
    markAsRead(conversation.id)

    // Stop notification sound if playing
    stopNotificationSound()

    // Clear unread indicators in local state
    setConversations(prev => prev.map(c =>
      c.id === conversation.id
        ? { ...c, unread_count: 0, isNew: false }
        : c
    ))

    // Join the conversation room via socket
    joinConversation(conversation.id, (response) => {
      if (response.success) {
        console.log('Joined conversation room:', conversation.id)
      } else {
        console.error('Failed to join conversation room:', response.error)
      }
    })

    try {
      // Fetch full message history
      const response = await getConversationMessages(conversation.id)
      let messages = []

      if (response && response.data) {
        if (Array.isArray(response.data)) {
          messages = response.data
        } else if (response.data.messages) {
          messages = response.data.messages
        } else if (response.data.data) {
          messages = response.data.data
        }
      }

      // Normalize attachments field for all messages
      messages = messages.map(msg => ({
        ...msg,
        attachments: Array.isArray(msg.attachments) ? msg.attachments : []
      }))

      // Update active conversation with fetched messages (only if still active)
      setActiveConversation(prev => {
        if (prev && prev.id === conversation.id) {
          return {
            ...prev,
            messages: messages
          }
        }
        return prev
      })

      // Update conversation in the list as well to cache it
      setConversations(prev => prev.map(c =>
        c.id === conversation.id ? { ...c, messages: messages } : c
      ))

    } catch (error) {
      console.error('Error fetching messages:', error)
    }
  }

  const handleSendMessage = (message, attachments = []) => {
    if (!activeConversation) return

    console.log('Sending message:', { message, attachments, conversationId: activeConversation.id })

    sendMessage(activeConversation.id, message, (response) => {
      console.log('Send message response:', response)

      if (response && response.success) {
        // Create a complete message object
        const newMessage = {
          id: response.message?.id || Date.now(), // Fallback to timestamp if no ID
          message: message,
          sender_type: 'agent',
          created_at: response.message?.created_at || new Date().toISOString(),
          conversation_id: activeConversation.id,
          attachments: Array.isArray(attachments) ? attachments : [],
          ...response.message // Spread any additional fields from response
        }

        console.log('Created new message object:', newMessage)

        // Update active conversation with new message
        setActiveConversation(prev => {
          // Check for duplicates
          const messageExists = prev.messages?.some(m => m.id === newMessage.id)
          if (messageExists) {
            console.log('Message already exists in active conversation')
            return prev
          }

          return {
            ...prev,
            messages: [...(prev.messages || []), newMessage]
          }
        })

        // Also update the conversation in the list
        setConversations(prev => prev.map(c => {
          if (c.id === activeConversation.id) {
            const messageExists = c.messages?.some(m => m.id === newMessage.id)
            if (!messageExists) {
              return {
                ...c,
                messages: [...(c.messages || []), newMessage]
              }
            }
          }
          return c
        }))
      } else {
        console.error('Failed to send message:', response)
      }
    })
  }

  const handleCloseConversation = () => {
    if (!activeConversation) return

    closeConversation(activeConversation.id, (response) => {
      if (response.success) {
        setConversations(prev => prev.filter(c => c.id !== activeConversation.id))
        setActiveConversation(null)
      }
    })
  }

  const handleTransferConversation = (conversationId, toAgentId) => {
    transferConversation(conversationId, toAgentId, (response) => {
      if (response.success) {
        // Update conversation status to pending in list (if it's still there)
        setConversations(prev => prev.map(c => {
          if (c.id === conversationId) {
            return {
              ...c,
              ...response.conversation,
              status: 'pending', // Or keep as assigned but to someone else? Usually transfer makes it pending for the other agent or assigned to them.
              assigned_agent_id: null, // It's no longer assigned to *us*
              transferredTo: response.conversation.assignedAgent || { first_name: 'Agent' } // Store for UI display
            }
          }
          return c
        }))

        // Clear active conversation if it was the transferred one
        if (activeConversation?.id === conversationId) {
          setActiveConversation(null)
        }

        alert('Conversation transferred! The receiving agent needs to accept it.')
      }
    })
  }

  const handleJoinConversation = (conversationId) => {
    console.log('Joining conversation:', conversationId)
    joinConversation(conversationId, (response) => {
      console.log('Join conversation response:', response)
      if (response.success) {
        // Update conversation in list with assigned data
        setConversations(prev => prev.map(c =>
          c.id === conversationId ? { ...c, ...response.conversation } : c
        ))
        // Set as active conversation
        setActiveConversation(response.conversation)
        alert('You have joined the conversation!')
      } else {
        alert(`Failed to join conversation: ${response.error || 'Unknown error'}`)
      }
    })
  }
  // console.log('user', user)

  const handleCustomerUpdate = (updatedDetails) => {
    // Update active conversation
    setActiveConversation(prev => ({
      ...prev,
      ...updatedDetails
    }))

    // Update conversation in the list
    setConversations(prev => prev.map(c =>
      c.id === activeConversation.id
        ? { ...c, ...updatedDetails }
        : c
    ))
  }

  return (
    <div className="chat-page-content">
      <div className="chat-layout">
        {/* Sidebar - Conversation List */}
        <div className="chat-sidebar">
          <div className="sidebar-header">
            <div className="d-flex justify-content-between align-items-center">
              <h4>Messages</h4>
              <Badge
                color={
                  !isConnected ? 'danger' :
                    agentStatus === 'online' ? 'success' :
                      agentStatus === 'away' ? 'warning' : 'secondary'
                }
                pill
                className="px-2"
              >
                {!isConnected ? 'Disconnected' :
                  agentStatus === 'online' ? 'Online' :
                    agentStatus === 'away' ? 'Away' : 'Offline'}
              </Badge>
            </div>
          </div>

          <div className="status-filters">
            <Button
              size="sm"
              color={statusFilter === 'active' ? 'primary' : 'light'}
              className={statusFilter === 'active' ? '' : 'text-muted'}
              onClick={() => setStatusFilter('active')}
              style={{ flex: 1 }}
            >
              Active
            </Button>
            <Button
              size="sm"
              color={statusFilter === 'all' ? 'primary' : 'light'}
              className={statusFilter === 'all' ? '' : 'text-muted'}
              onClick={() => setStatusFilter('all')}
              style={{ flex: 1 }}
            >
              All
            </Button>
          </div>

          <ConversationList
            user={user}
            conversations={conversations}
            activeConversation={activeConversation}
            onSelectConversation={handleSelectConversation}
            onJoinConversation={handleJoinConversation}
            loading={loading}
          />

          <div className="p-3 border-top mt-auto bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">Status:</small>
              <div className="btn-group btn-group-sm">
                <Button
                  color={agentStatus === 'online' ? 'success' : 'outline-secondary'}
                  onClick={() => handleStatusChange('online')}
                  active={agentStatus === 'online'}
                >
                  Online
                </Button>
                <Button
                  color={agentStatus === 'away' ? 'warning' : 'outline-secondary'}
                  onClick={() => handleStatusChange('away')}
                  active={agentStatus === 'away'}
                >
                  Away
                </Button>
                <Button
                  color={agentStatus === 'offline' ? 'secondary' : 'outline-secondary'}
                  onClick={() => handleStatusChange('offline')}
                  active={agentStatus === 'offline'}
                >
                  Offline
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="chat-main">
          <MessagePanel
            conversation={activeConversation}
            onSendMessage={handleSendMessage}
            onCloseConversation={handleCloseConversation}
            onTransferConversation={handleTransferConversation}
            typingUsers={typingUsers}
            onSetTyping={setTyping}
          />
        </div>

        {/* Right Sidebar - Customer Info */}
        <div className="chat-info-sidebar">
          <CustomerInfo
            conversation={activeConversation}
            onUpdate={handleCustomerUpdate}
          />
        </div>
      </div>
    </div>
  )
}

export default Chat
